import cookie from 'react-cookies';
import download from 'downloadjs';
import * as mime from 'react-native-mime-types';

export default class API
{
    /**
     * Log a user out of the application
     *
     * @param {*} onComplete
     */
    logout(onComplete)
    {
        this.clearCookies();

        return typeof onComplete === 'function' ? onComplete() : null;
    }

    /**
     * Check that a set of password reset credentials are valid
     *
     * @param {*} userId
     * @param {*} key
     * @param {*} onSuccess
     * @param {*} onError
     */
    checkPasswordResetKey(userId, key, onSuccess, onError)
    {
        this.request(
            'GET',
            '/auth/reset-password/' + userId + '/' + key,
            null,
            null,
            onSuccess,
            onError
        );
    }

    /**
     * Change a user's password using a set of password reset credentials
     *
     * @param {*} userId
     * @param {*} key
     * @param {*} password
     * @param {*} onSuccess
     * @param {*} onError
     */
    changePassword(userId, key, password, onSuccess, onError)
    {
        this.request(
            'POST',
            '/auth/reset-password/' + userId + '/' + key,
            {
                password: password
            },
            null,
            (response)=>{
                this.storeAuthData(response);

                onSuccess(response);
            },
            onError
        );
    }

    /**
     * Make a request
     *
     * @param {*} method
     * @param {*} edge
     * @param {*} params
     * @param {*} headers
     * @param {*} onSuccess
     * @param {*} onError
     * @param {*} withAuth
     */
    request(method, edge, params = {}, headers = {})
    {
        method  = method.trim().toUpperCase(); // Normalize method
        headers =  new Headers(Object.assign({
            'X-Customer-Id': cookie.load('user')?.id,
            'X-Customer-Token': cookie.load('auth')?.token
        }, headers));

        var init = {
            headers: headers,
        }

        params = Object.assign({}, params);

        //
        //  Format parameters
        //

        if( method === 'POST' ){
            //  Sent POST requests as form data
            let body = new FormData();
            for (let p in params) {
                let value = params[p];
                value = value === null || value === undefined ? '' : value;
                if (Array.isArray(value)) {
                    value.forEach(file => {
                        if (file instanceof File) {
                            body.append(p + '[]', file); // Ensure files are appended correctly
                        }
                    });
                } else if (typeof value === 'object' && !(value instanceof File)) {
                    value = JSON.stringify(value);
                    body.append(p, value);
                } else if (typeof value === 'boolean') {
                    body.append(p, value ? 1 : 0);
                } else {
                    body.append(p, value);
                }
            }
            init.body = body;
        }else{
            const keys = Object.keys(params);
            if(keys.length){
                //  Send all other request types as a url string
                let data = keys.map((p)=>{
                    return p + '=' + encodeURIComponent(params[p])
                }).join('&');
                edge += (edge.includes('?') ?  '&' : '?') + data;
            }
        }

        init.method = method;

        return fetch(this.edge(edge), init)
               .then((response)=>{
                    if(response.status === 401){
                        return response;
                    }

                    if (response.status === 403) {
                        this.logout(()=>{ window.location = '/login' });
                    }

                    if(response.status >= 500){
                        throw new Error('Server error.');
                    }

                    return response;
                });
    }

    json(method, edge, params, headers = {})
    {
        headers.Accept = 'application/json';

        return this.request(method, edge, params, headers);
    }

    /**
     * Downloads a file from a URL
     *
     * @param {*} url
     * @param {*} fileName
     * @param {*} mimeType
     */
    download(url, params, fileName = 'Download'){
        return this.request('GET', url, params)
                    .then(async (responseData)=>{
                        const ext = fileName.includes('.') ? '' : '.xlsx';
                        const mimeType = mime.lookup(fileName);

                        if( responseData.error )
                            return Promise.reject(responseData.error);
                        download(await responseData.arrayBuffer(), fileName + ext, mimeType);
                    })
    }

    raw(method, edge, params, headers)
    {
        //  Start prepping the request data
        method  = method.trim().toUpperCase(); // Normalize method
        headers = headers || {};
        var init = {
            method: method,
            headers: Object.assign({
                'Accept': 'application/json',
                'Authorization' : 'Bearer ' + cookie.load('auth_token')
            }, headers || {}),
        }

        //  Format parameters
        if( params && typeof params === 'object' ){
            if( method === 'POST' ){
                //  Sent POST requests as form data
                let sendParams = new FormData();
                for(var p in params)
                    sendParams.append(p, params[p]);
                    init['body'] = sendParams;
            }else{
                //  Send all other request types as a url st
                let data = '';
                for(var param in params)
                    data += param + '=' + encodeURIComponent(params[param]) + '&';

                data  = data.replace(/&$/, '');
                edge += ! /\?$/.test(edge) ? '?' : '&';
                edge += data;
            }
        }

        return fetch(this.edge(edge), init);
    }

    /**
     * Make a get request with auth
     *
     * @param {*} edge
     * @param {*} params
     * @param {*} headers
     * @param {*} onSuccess
     * @param {*} onError
     * @param {*} withAuth
     */
    get(edge, params, headers, onSuccess = null, onError = null, always = null)
    {
        return this.request('GET', edge, params, headers, onSuccess, onError, always, true);
    }

    /**
     * Make a post request with auth
     *
     * @param {*} edge
     * @param {*} params
     * @param {*} headers
     * @param {*} onSuccess
     * @param {*} onError
     * @param {*} withAuth
     */
    post(edge, params, headers, onSuccess = null, onError = null, always = null)
    {
        return this.request('POST', edge, params, headers, onSuccess, onError, always, true);
    }

    /**
     * Make a put request with auth
     *
     * @param {*} edge
     * @param {*} params
     * @param {*} headers
     * @param {*} onSuccess
     * @param {*} onError
     * @param {*} withAuth
     */
    put(edge, params, headers, onSuccess = null, onError = null, always = null)
    {
        params         = params || {};
        params._method = 'PUT';

        return this.request('POST', edge, params, headers, onSuccess, onError, always, true);
    }

    /**
     * Make a delete request with auth
     *
     * @param {*} edge
     * @param {*} params
     * @param {*} headers
     * @param {*} onSuccess
     * @param {*} onError
     * @param {*} withAuth
     */
    delete(edge, params, headers, onSuccess = null, onError = null, always = null)
    {
        params         = params || {};
        params._method = 'DELETE';

        return this.request('POST', edge, params, headers, onSuccess, onError, always, true);
    }

    /**
     * Return a complete URL for an edge
     *
     * @param {*} path
     */
    edge(path)
    {
        let edge = process.env.REACT_APP_API_URL.replace(/\/$/, '');

        path = path.replace(/^\//, '');
        path = path.replace(/\/$/, '');

        return  edge + '/' + path;
    }

    /**
     * Store auth credentials from an auth request
     *
     * @param {*} authResponse
     */
    storeAuthData(authResponse)
    {
        this.cookie('auth', authResponse.auth);
        this.cookie('user', authResponse.customer);
        this.cookie('systems', authResponse.systems);
        this.cookie('system', authResponse.systems[0] || null);
        this.cookie('session_settings', {});
        this.cookie('incomplete_systems_agreements', authResponse.incomplete_systems_agreements || []);
    }

    /**
     * update systems data
     *
     * @param {*} systems
     */
    updateSystemsData(systems)
    {
        this.cookie('systems', systems);
    }

    /**
     * update system data
     *
     * @param {*} system
     */
    updateSystemData(system)
    {
        this.cookie('system', system);
    }

    /**
     * update incomplete systems agreements data
     *
     * @param {*} data
     */
    updateIncompleteSystemsAgreementsData(data)
    {
        this.cookie('incomplete_systems_agreements', data);
    }

    /**
     * update incomplete systems agreements data
     *
     * @param {*} value
     */
    updateUserSawWelcomePage(value)
    {
        this.cookie('user_saw_welcome_page', value);
    }

    /**
     * update show plaid link bank account pop up
     *
     * @param {*} value
     */
    updateShowPlaidLinkBankAccountPopUp(value)
    {
        this.cookie('show_plaid_link_bank_account_pop_up', value);
    }

    /**
     * Setting for current session
     *
     * @param settings
     */
    setSessionSettings(settings = {})
    {
        this.cookie('session_settings', settings)
    }

    /**
     * Get or set a cookie
     *
     * @param {*} name
     * @param {*} value
     */
    cookie(name, value, expiration)
    {
        if( typeof value === 'undefined' ) // Get cookie if no value is provided
            return cookie.load(name);

        let cookieOpts = {
            path: '/',
            maxAge: typeof expiration !== 'undefined' ? expiration : (60 * 60 * 24 * 365),
        };

        cookie.save(name, value, cookieOpts);
    }

    /**
     * Remove a cookie
     *
     */
    removeCookie(name)
    {
        cookie.remove(name, {
            path: '/'
        });
    }

    clearCookies()
    {
        let cookies = cookie.loadAll();
        for(var cookieName in cookies){
            this.removeCookie(cookieName);
        }
    }
}
